import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  Animated,
  Modal,
  Platform,
  StyleSheet,
  Text,
  TextInput,
  TouchableOpacity,
  View,
} from 'react-native';

import { KeyboardDismissToolbar } from '../../components/shared/KeyboardDismissToolbar';
import { KeyboardInsetSheet } from '../../components/shared/keyboardInset';
import { colors } from '../../theme';
import {
  formatTimeZoneOptionLabel,
  listIanaTimeZoneOptions,
} from '../../utils/ianaTimeZones';

const TIME_ZONE_ACCESSORY_VIEW_ID = 'preferences-timezone-keyboard-dismiss';

interface TimeZoneFieldsProps {
  timeZone: string;
  syncWithDevice: boolean;
  fieldDisabled?: boolean;
  switchDisabled?: boolean;
  onTimeZoneChange: (timeZone: string) => void;
  onSyncChange: (syncWithDevice: boolean) => void;
}

export function TimeZoneFields({
  timeZone,
  syncWithDevice,
  fieldDisabled = false,
  switchDisabled = false,
  onTimeZoneChange,
  onSyncChange,
}: TimeZoneFieldsProps): React.ReactElement {
  const zones = useMemo(() => listIanaTimeZoneOptions(), []);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [filter, setFilter] = useState('');
  const animatedValue = useRef(
    new Animated.Value(syncWithDevice ? 1 : 0),
  ).current;

  useEffect(() => {
    Animated.timing(animatedValue, {
      toValue: syncWithDevice ? 1 : 0,
      duration: 200,
      useNativeDriver: false,
    }).start();
  }, [animatedValue, syncWithDevice]);

  const thumbTranslateX = animatedValue.interpolate({
    inputRange: [0, 1],
    outputRange: [0, 20],
  });
  const trackColor = animatedValue.interpolate({
    inputRange: [0, 1],
    outputRange: ['#ccc', '#FF6B35'],
  });

  const filtered = useMemo(() => {
    const q = filter.trim().toLowerCase();
    if (!q) {
      return zones;
    }
    const query = q.replace(/[_/]/g, ' ');
    return zones.filter(zone =>
      zone.label.toLowerCase().replace(/[_/]/g, ' ').includes(query),
    );
  }, [filter, zones]);

  const closePicker = () => {
    setPickerOpen(false);
    setFilter('');
  };

  const selectedOption = zones.find(zone => zone.id === timeZone);
  const highlightedId = selectedOption ? timeZone : undefined;
  const selectedLabel = selectedOption
    ? selectedOption.label
    : timeZone
    ? formatTimeZoneOptionLabel(timeZone)
    : 'Select a time zone';

  return (
    <View style={styles.zoneBlock} testID="preferences-timezone-fields">
      <Text style={styles.fieldLabel}>Time zone</Text>
      <TouchableOpacity
        style={[styles.select, fieldDisabled && styles.selectDisabled]}
        onPress={() => {
          if (!fieldDisabled) {
            setPickerOpen(true);
          }
        }}
        disabled={fieldDisabled}
        testID="preferences-timezone-select"
        accessibilityRole="button"
        accessibilityLabel="Time zone"
        accessibilityState={{ disabled: fieldDisabled }}
      >
        <Text
          style={[
            styles.selectText,
            fieldDisabled && styles.selectTextDisabled,
          ]}
        >
          {selectedLabel}
        </Text>
        <Text style={styles.chevron}>▼</Text>
      </TouchableOpacity>

      <View style={styles.syncRow}>
        <Text style={styles.syncTitle}>Sync with device settings</Text>
        <TouchableOpacity
          testID="preferences-timezone-sync"
          accessibilityRole="switch"
          accessibilityLabel="Sync with device settings"
          accessibilityState={{
            checked: syncWithDevice,
            disabled: switchDisabled,
          }}
          onPress={() => {
            if (!switchDisabled) {
              onSyncChange(!syncWithDevice);
            }
          }}
          disabled={switchDisabled}
          activeOpacity={0.7}
          style={[styles.toggleTrack, switchDisabled && styles.toggleDisabled]}
        >
          <Animated.View
            style={[styles.toggleTrackInner, { backgroundColor: trackColor }]}
          >
            <Animated.View
              style={[
                styles.toggleThumb,
                { transform: [{ translateX: thumbTranslateX }] },
              ]}
            />
          </Animated.View>
        </TouchableOpacity>
      </View>

      <Modal
        visible={pickerOpen}
        animationType="slide"
        transparent
        statusBarTranslucent
        onRequestClose={closePicker}
      >
        <KeyboardDismissToolbar nativeID={TIME_ZONE_ACCESSORY_VIEW_ID} />
        <KeyboardInsetSheet
          onBackdropPress={closePicker}
          header={
            <>
              <Text style={styles.modalTitle}>Time zone</Text>
              <TextInput
                style={styles.filterInput}
                value={filter}
                onChangeText={setFilter}
                placeholder="Search zones"
                placeholderTextColor={colors.inputPlaceholder}
                testID="preferences-timezone-filter"
                autoCorrect={false}
                autoCapitalize="none"
                inputAccessoryViewID={
                  Platform.OS === 'ios'
                    ? TIME_ZONE_ACCESSORY_VIEW_ID
                    : undefined
                }
              />
            </>
          }
          cardStyle={[
            styles.modalCard,
            Platform.OS === 'web' && styles.modalCardWeb,
          ]}
          idleMaxHeight={Platform.OS === 'web' ? 520 : '80%'}
          listTestID="preferences-timezone-options"
        >
          {filtered.map(item => (
            <TouchableOpacity
              key={item.id}
              style={styles.option}
              testID={`preferences-timezone-option-${item.id}`}
              onPress={() => {
                onTimeZoneChange(item.id);
                setPickerOpen(false);
                setFilter('');
              }}
            >
              <Text
                style={[
                  styles.optionText,
                  item.id === highlightedId && styles.optionSelected,
                ]}
              >
                {item.label}
              </Text>
            </TouchableOpacity>
          ))}
        </KeyboardInsetSheet>
      </Modal>
    </View>
  );
}

const styles = StyleSheet.create({
  zoneBlock: {
    marginTop: 4,
    marginBottom: 8,
  },
  fieldLabel: {
    fontSize: 13,
    fontWeight: '500',
    color: colors.textSecondary,
    marginBottom: 6,
    textTransform: 'uppercase',
    letterSpacing: 0.5,
  },
  select: {
    backgroundColor: colors.backgroundPrimary,
    borderRadius: 6,
    padding: 12,
    borderWidth: 1,
    borderColor: colors.inputBorder,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    minHeight: 42,
  },
  selectDisabled: {
    opacity: 0.45,
  },
  selectText: {
    fontSize: 15,
    color: colors.textPrimary,
    flex: 1,
  },
  selectTextDisabled: {
    color: colors.textSecondary,
  },
  chevron: {
    fontSize: 10,
    color: colors.textSecondary,
    marginLeft: 8,
  },
  syncRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 16,
    marginTop: 16,
  },
  syncTitle: {
    flex: 1,
    fontSize: 16,
    fontWeight: '500',
    color: '#333',
  },
  toggleDisabled: {
    opacity: 0.5,
  },
  toggleTrack: {
    width: 50,
    height: 30,
    borderRadius: 15,
    overflow: 'hidden',
    ...Platform.select({
      web: {
        cursor: 'pointer' as const,
      },
    }),
  },
  toggleTrackInner: {
    width: '100%',
    height: '100%',
    borderRadius: 15,
    justifyContent: 'center',
    padding: 2,
  },
  toggleThumb: {
    width: 26,
    height: 26,
    borderRadius: 13,
    backgroundColor: '#fff',
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.2,
    shadowRadius: 2,
    elevation: 2,
  },
  modalCard: {
    backgroundColor: colors.backgroundPrimary,
    borderTopLeftRadius: 12,
    borderTopRightRadius: 12,
    borderBottomLeftRadius: 0,
    borderBottomRightRadius: 0,
    padding: 16,
  },
  modalCardWeb: {
    width: '100%',
    maxWidth: 480,
    alignSelf: 'center',
    borderBottomLeftRadius: 12,
    borderBottomRightRadius: 12,
  },
  modalTitle: {
    fontSize: 18,
    fontWeight: '600',
    color: colors.textPrimary,
    marginBottom: 12,
  },
  filterInput: {
    borderWidth: 1,
    borderColor: colors.inputBorder,
    borderRadius: 6,
    padding: 10,
    marginBottom: 8,
    fontSize: 15,
    color: colors.textPrimary,
  },
  option: {
    paddingVertical: 12,
    borderBottomWidth: StyleSheet.hairlineWidth,
    borderBottomColor: '#e9ecef',
  },
  optionText: {
    fontSize: 15,
    color: colors.textPrimary,
  },
  optionSelected: {
    color: colors.primary,
    fontWeight: '600',
  },
});
